import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { linkedInAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface ProblemData {
  title: string;
  count: number;
  recommendation: string;
}

export interface AccountData {
  name: string;
  headline: string;
  connections: number;
  followers: number;
  posts: number;
  profilePicture: string;
  company: string;
  location: string;
  industry: string;
  isVerified: boolean;
  summary: string;
}

export interface PostData {
  id: number;
  content: string;
  likes: number;
  comments: number;
  shares: number;
  reach: number;
  engagement: number;
  postedAt: string;
  type: string;
}

export interface LinkedInState {
  account: AccountData | null;
  recentPosts: PostData[];
  topContent: PostData[];
  engagement: ChartData | null;
  reach: ChartData | null;
  hashtags: ChartData | null;
  contentPerformance: ChartData | null;
  sentiment: ChartData | null;
  problems: ProblemData[];
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    account: boolean;
    recentPosts: boolean;
    topContent: boolean;
    engagement: boolean;
    reach: boolean;
    hashtags: boolean;
    contentPerformance: boolean;
    sentiment: boolean;
    problems: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    account: string | null;
    recentPosts: string | null;
    topContent: string | null;
    engagement: string | null;
    reach: string | null;
    hashtags: string | null;
    contentPerformance: string | null;
    sentiment: string | null;
    problems: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type LinkedInAction =
  | { type: 'FETCH_ACCOUNT_START' }
  | { type: 'FETCH_ACCOUNT_SUCCESS'; payload: AccountData }
  | { type: 'FETCH_ACCOUNT_ERROR'; payload: string }
  | { type: 'FETCH_RECENT_POSTS_START' }
  | { type: 'FETCH_RECENT_POSTS_SUCCESS'; payload: PostData[] }
  | { type: 'FETCH_RECENT_POSTS_ERROR'; payload: string }
  | { type: 'FETCH_TOP_CONTENT_START' }
  | { type: 'FETCH_TOP_CONTENT_SUCCESS'; payload: PostData[] }
  | { type: 'FETCH_TOP_CONTENT_ERROR'; payload: string }
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_REACH_START' }
  | { type: 'FETCH_REACH_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_REACH_ERROR'; payload: string }
  | { type: 'FETCH_HASHTAGS_START' }
  | { type: 'FETCH_HASHTAGS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_HASHTAGS_ERROR'; payload: string }
  | { type: 'FETCH_CONTENT_PERFORMANCE_START' }
  | { type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_CONTENT_PERFORMANCE_ERROR'; payload: string }
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_PROBLEMS_START' }
  | { type: 'FETCH_PROBLEMS_SUCCESS'; payload: ProblemData[] }
  | { type: 'FETCH_PROBLEMS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: LinkedInState = {
  account: null,
  recentPosts: [],
  topContent: [],
  engagement: null,
  reach: null,
  hashtags: null,
  contentPerformance: null,
  sentiment: null,
  problems: [],
  aiAnalysis: {},
  loading: {
    account: false,
    recentPosts: false,
    topContent: false,
    engagement: false,
    reach: false,
    hashtags: false,
    contentPerformance: false,
    sentiment: false,
    problems: false,
    aiAnalysis: {},
  },
  error: {
    account: null,
    recentPosts: null,
    topContent: null,
    engagement: null,
    reach: null,
    hashtags: null,
    contentPerformance: null,
    sentiment: null,
    problems: null,
    aiAnalysis: {},
  },
};

const linkedInReducer = (state: LinkedInState, action: LinkedInAction): LinkedInState => {
  switch (action.type) {
    case 'FETCH_ACCOUNT_START':
      return {
        ...state,
        loading: { ...state.loading, account: true },
        error: { ...state.error, account: null },
      };
    case 'FETCH_ACCOUNT_SUCCESS':
      return {
        ...state,
        account: action.payload,
        loading: { ...state.loading, account: false },
      };
    case 'FETCH_ACCOUNT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, account: false },
        error: { ...state.error, account: action.payload },
      };
    case 'FETCH_RECENT_POSTS_START':
      return {
        ...state,
        loading: { ...state.loading, recentPosts: true },
        error: { ...state.error, recentPosts: null },
      };
    case 'FETCH_RECENT_POSTS_SUCCESS':
      return {
        ...state,
        recentPosts: action.payload,
        loading: { ...state.loading, recentPosts: false },
      };
    case 'FETCH_RECENT_POSTS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recentPosts: false },
        error: { ...state.error, recentPosts: action.payload },
      };
    case 'FETCH_TOP_CONTENT_START':
      return {
        ...state,
        loading: { ...state.loading, topContent: true },
        error: { ...state.error, topContent: null },
      };
    case 'FETCH_TOP_CONTENT_SUCCESS':
      return {
        ...state,
        topContent: action.payload,
        loading: { ...state.loading, topContent: false },
      };
    case 'FETCH_TOP_CONTENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, topContent: false },
        error: { ...state.error, topContent: action.payload },
      };
    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };
    case 'FETCH_REACH_START':
      return {
        ...state,
        loading: { ...state.loading, reach: true },
        error: { ...state.error, reach: null },
      };
    case 'FETCH_REACH_SUCCESS':
      return {
        ...state,
        reach: action.payload,
        loading: { ...state.loading, reach: false },
      };
    case 'FETCH_REACH_ERROR':
      return {
        ...state,
        loading: { ...state.loading, reach: false },
        error: { ...state.error, reach: action.payload },
      };
    case 'FETCH_HASHTAGS_START':
      return {
        ...state,
        loading: { ...state.loading, hashtags: true },
        error: { ...state.error, hashtags: null },
      };
    case 'FETCH_HASHTAGS_SUCCESS':
      return {
        ...state,
        hashtags: action.payload,
        loading: { ...state.loading, hashtags: false },
      };
    case 'FETCH_HASHTAGS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, hashtags: false },
        error: { ...state.error, hashtags: action.payload },
      };
    case 'FETCH_CONTENT_PERFORMANCE_START':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: true },
        error: { ...state.error, contentPerformance: null },
      };
    case 'FETCH_CONTENT_PERFORMANCE_SUCCESS':
      return {
        ...state,
        contentPerformance: action.payload,
        loading: { ...state.loading, contentPerformance: false },
      };
    case 'FETCH_CONTENT_PERFORMANCE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: false },
        error: { ...state.error, contentPerformance: action.payload },
      };
    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };
    case 'FETCH_PROBLEMS_START':
      return {
        ...state,
        loading: { ...state.loading, problems: true },
        error: { ...state.error, problems: null },
      };
    case 'FETCH_PROBLEMS_SUCCESS':
      return {
        ...state,
        problems: action.payload,
        loading: { ...state.loading, problems: false },
      };
    case 'FETCH_PROBLEMS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, problems: false },
        error: { ...state.error, problems: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface LinkedInContextType {
  state: LinkedInState;
  fetchAccountData: () => Promise<void>;
  fetchRecentPosts: () => Promise<void>;
  fetchTopContent: () => Promise<void>;
  fetchEngagementData: () => Promise<void>;
  fetchReachData: () => Promise<void>;
  fetchHashtagsData: () => Promise<void>;
  fetchContentPerformanceData: () => Promise<void>;
  fetchSentimentData: () => Promise<void>;
  fetchProblemsData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
}

const LinkedInContext = createContext<LinkedInContextType | undefined>(undefined);

export const LinkedInProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(linkedInReducer, initialState);
  const { business } = useAuth();

  const fetchAccountData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ACCOUNT_START' });
    try {
      const response = await linkedInAPI.getAccountData(business._id);
      const accountData = response.data.data;
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Apply proxy to profile picture if it exists
      if (accountData && typeof accountData === 'object') {
        accountData.profilePicture = toProxy(accountData.profilePicture);
      }
      
      dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: accountData });
    } catch (error) {
      dispatch({ type: 'FETCH_ACCOUNT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchRecentPosts = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_RECENT_POSTS_START' });
    try {
      const response = await linkedInAPI.getRecentPosts(business._id);
      dispatch({ type: 'FETCH_RECENT_POSTS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_RECENT_POSTS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchTopContent = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_TOP_CONTENT_START' });
    try {
      const response = await linkedInAPI.getTopContent(business._id);
      dispatch({ type: 'FETCH_TOP_CONTENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_TOP_CONTENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ENGAGEMENT_START' });
    try {
      const response = await linkedInAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchReachData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_REACH_START' });
    try {
      const response = await linkedInAPI.getReachData(business._id);
      dispatch({ type: 'FETCH_REACH_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_REACH_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_SENTIMENT_START' });
    try {
      const response = await linkedInAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await linkedInAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  const fetchProblemsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_PROBLEMS_START' });
    try {
      const response = await linkedInAPI.getProblemsData(business._id);
      dispatch({ type: 'FETCH_PROBLEMS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'Problem verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchHashtagsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_HASHTAGS_START' });
    try {
      const response = await linkedInAPI.getHashtagsData(business._id);
      dispatch({ type: 'FETCH_HASHTAGS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'Hashtag verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchContentPerformanceData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_START' });
    try {
      const response = await linkedInAPI.getContentPerformanceData(business._id);
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_ERROR', payload: 'İçerik performans verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  return (
    <LinkedInContext.Provider
      value={{
        state,
        fetchAccountData,
        fetchRecentPosts,
        fetchTopContent,
        fetchEngagementData,
        fetchReachData,
        fetchHashtagsData,
        fetchContentPerformanceData,
        fetchSentimentData,
        fetchProblemsData,
        fetchAIAnalysis,
      }}
    >
      {children}
    </LinkedInContext.Provider>
  );
};

export const useLinkedIn = (): LinkedInContextType => {
  const context = useContext(LinkedInContext);
  if (context === undefined) {
    throw new Error('useLinkedIn must be used within a LinkedInProvider');
  }
  return context;
};
